import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, ShieldCheckIcon, BarChart3Icon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { TrustScore } from '../components/TrustScore';
import { cryptocurrencies, exchanges } from '../data/mockData';
export function HomePage() {
  const topCryptos = cryptocurrencies.slice(0, 5);
  const topExchanges = exchanges.slice(0, 5);
  return <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-id="element-336">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20" data-id="element-337">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-338">
          <div className="text-center max-w-3xl mx-auto" data-id="element-339">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-2 mb-6" data-id="element-340">
              <ShieldCheckIcon size={18} data-id="element-341" />
              <span className="text-sm font-medium" data-id="element-342">
                Trusted Crypto Intelligence
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-343">
              The TrustPilot for{' '}
              <span className="text-blue-400" data-id="element-344">Cryptocurrency</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed" data-id="element-345">
              Comprehensive reviews, trust scores, and intelligence for
              cryptocurrencies and exchanges. Make informed decisions with
              verified data.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-346">
              <Link to="/cryptocurrencies" data-id="element-347">
                <Button size="lg" className="shadow-lg shadow-blue-500/20" data-id="element-348">
                  Explore Cryptocurrencies
                </Button>
              </Link>
              <Link to="/exchanges" data-id="element-349">
                <Button variant="outline" size="lg" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-id="element-350">
                  View Exchanges
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-352">
          <Card className="p-6 text-center" hover data-id="element-353">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-354">
              <ShieldCheckIcon className="text-blue-600" size={24} data-id="element-355" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-356">Trust Scores</h3>
            <p className="text-slate-600" data-id="element-357">
              Proprietary trust scoring system based on security, transparency,
              and user reviews.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-358">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-359">
              <BarChart3Icon className="text-green-600" size={24} data-id="element-360" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-361">Market Data</h3>
            <p className="text-slate-600" data-id="element-362">
              Real-time pricing, market cap, volume, and performance metrics for
              informed decisions.
            </p>
          </Card>

          <Card className="p-6 text-center" hover data-id="element-363">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4" data-id="element-364">
              <TrendingUpIcon className="text-purple-600" size={24} data-id="element-365" />
            </div>
            <h3 className="font-semibold text-lg mb-2" data-id="element-366">Verified Reviews</h3>
            <p className="text-slate-600" data-id="element-367">
              Authentic user reviews from verified traders and investors in the
              crypto space.
            </p>
          </Card>
        </div>
      </section>

      {/* Top Cryptocurrencies Preview */}
      <section className="py-16 bg-slate-50" data-id="element-368">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-369">
          <div className="flex items-center justify-between mb-8" data-id="element-370">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-371">
              Top Cryptocurrencies
            </h2>
            <Link to="/cryptocurrencies" data-id="element-372">
              <Button variant="outline" data-id="element-373">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4" data-id="element-374">
            {topCryptos.map(crypto => <Link key={crypto.id} to={`/cryptocurrency/${crypto.id}`} data-id="element-375">
                <Card className="p-5" hover data-id="element-376">
                  <div className="flex items-center justify-between" data-id="element-377">
                    <div className="flex items-center gap-4" data-id="element-378">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold" data-id="element-379">
                        {crypto.rank}
                      </div>
                      <div data-id="element-380">
                        <div className="flex items-center gap-2 mb-1" data-id="element-381">
                          <h3 className="font-semibold text-lg" data-id="element-382">
                            {crypto.name}
                          </h3>
                          <span className="text-slate-500 text-sm" data-id="element-383">
                            {crypto.symbol}
                          </span>
                        </div>
                        <TrustScore score={crypto.trustScore} size="sm" data-id="element-384" />
                      </div>
                    </div>

                    <div className="text-right" data-id="element-385">
                      <div className="font-semibold text-lg mb-1" data-id="element-386">
                        ${crypto.price.toLocaleString()}
                      </div>
                      <div className={`text-sm font-medium ${crypto.change24h >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-387">
                        {crypto.change24h >= 0 ? '+' : ''}
                        {crypto.change24h.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Top Exchanges Preview */}
      <section className="py-16" data-id="element-388">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-389">
          <div className="flex items-center justify-between mb-8" data-id="element-390">
            <h2 className="text-3xl font-bold text-slate-900" data-id="element-391">Top Exchanges</h2>
            <Link to="/exchanges" data-id="element-392">
              <Button variant="outline" data-id="element-393">View All</Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-394">
            {topExchanges.map(exchange => <Link key={exchange.id} to={`/exchange/${exchange.id}`} data-id="element-395">
                <Card className="p-5" hover data-id="element-396">
                  <div className="flex items-center gap-3 mb-4" data-id="element-397">
                    <div className="w-10 h-10 bg-gradient-to-br from-slate-700 to-slate-800 rounded-lg flex items-center justify-center text-white font-bold" data-id="element-398">
                      {exchange.rank}
                    </div>
                    <div data-id="element-399">
                      <h3 className="font-semibold text-lg" data-id="element-400">{exchange.name}</h3>
                      <TrustScore score={exchange.trustScore} size="sm" data-id="element-401" />
                    </div>
                  </div>

                  <div className="space-y-2 text-sm" data-id="element-402">
                    <div className="flex justify-between" data-id="element-403">
                      <span className="text-slate-600" data-id="element-404">24h Volume:</span>
                      <span className="font-medium" data-id="element-405">
                        ${(exchange.tradingVolume / 1000000000).toFixed(1)}B
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-406">
                      <span className="text-slate-600" data-id="element-407">Coins:</span>
                      <span className="font-medium" data-id="element-408">
                        {exchange.supportedCoins}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-409">
                      <span className="text-slate-600" data-id="element-410">Trading Fee:</span>
                      <span className="font-medium" data-id="element-411">{exchange.tradingFee}</span>
                    </div>
                  </div>
                </Card>
              </Link>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-700 text-white" data-id="element-412">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-413">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-414">
            Ready to Make Informed Decisions?
          </h2>
          <p className="text-xl text-blue-100 mb-8" data-id="element-415">
            Join thousands of investors who trust CryptoTopReviews for their
            crypto intelligence.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-416">
            <Link to="/cryptocurrencies" data-id="element-417">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50" data-id="element-418">
                Start Exploring
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>;
}